<script setup lang="ts">
import SearchImageItem from "@/components/ai-image/SearchImageItem.vue";
import { useImageQueries } from "@/composables/ai-image/useImageQueries";
import { useDirectUploadShrine } from "@/composables/useDirectUploadShrine";
import { type FileInput } from "@/services/fobizzToolsApi";
import { AiImage } from "@/services/fobizzToolsApi";
import debounce from "lodash.debounce";

const { t } = useI18n();

defineProps<{
  open: boolean;
}>();

const emit = defineEmits([
  "close",
  "submit",
  "update:image",
  "update:progress",
]);

const LIMIT = 9;

const { useInfiniteIndexQuery } = useImageQueries();
const page = ref(1);
const {
  data: paginatedImages,
  hasNextPage,
  fetchNextPage,
  isFetching,
} = useInfiniteIndexQuery({
  page: page,
  limit: LIMIT,
});

const loadMore = debounce(() => {
  if (hasNextPage.value) {
    fetchNextPage();
  }
}, 500);

const selectedImage = ref<AiImage | null>(null);
const progress = ref(0);
const isSubmitting = ref(false);

const handleHide = () => {
  emit("close");
};

const { uploadFile } = useDirectUploadShrine();

const handleSubmit = async () => {
  if (!selectedImage.value || !selectedImage.value.file) {
    return;
  }

  isSubmitting.value = true;
  const request = await fetch(selectedImage.value.file.url);
  const blob = await request.blob();

  const file = new File([blob], selectedImage.value.file.filename, {
    type: "image/jpeg",
  });

  await uploadFile(file, {
    errorHandler: (error: Error) => {
      throw error;
    },
    successHandler: async (file: FileInput) => {
      emit("submit", file);
    },
    progressHandler: (_progress) => {
      progress.value = _progress;
      emit("update:progress", progress.value);
    },
  });
};

const handleSelectImage = (image: AiImage) => {
  selectedImage.value = image;
};
</script>

<template>
  <teleport to="body">
    <BaseModal size="lg" :open="open" @hide="handleHide">
      <div class="modal-header">
        <h5 class="modal-title">{{ t("headers.aiImageSearch") }}</h5>

        <button
          type="button"
          data-bs-dismiss="modal"
          class="btn-close"
          aria-label="Close"
        ></button>
      </div>
      <div class="modal-body search-modal-body">
        <form id="form" @submit.prevent="handleSubmit">
          <div
            v-if="paginatedImages && paginatedImages.pages[0].collection.length"
            class="ai-image-grid"
          >
            <template
              v-for="imagePage in paginatedImages.pages"
              :key="imagePage.currentPage"
            >
              <SearchImageItem
                v-for="image in imagePage.collection"
                :key="image.id"
                :image="image"
                :selected="selectedImage?.id === image.id"
                @click="handleSelectImage(image)"
              />
            </template>
          </div>
          <div v-if="isFetching" class="d-flex justify-content-center py-5">
            <BaseSpinner size="lg" />
          </div>

          <BaseObserver
            v-if="!isFetching && hasNextPage"
            @intersect="loadMore"
          />
        </form>
      </div>
      <div class="modal-footer">
        <a
          href="#"
          class="btn btn-link btn-link--dark"
          @click.prevent="handleHide"
        >
          {{ t("buttons.close") }}
        </a>
        <button type="submit" class="btn btn-dark btn-rounded" form="form">
          <div
            v-if="isSubmitting"
            class="spinner-border spinner-border-sm me-2"
            role="status"
          >
            <span class="visually-hidden">{{ t("labels.loading") }}</span>
          </div>

          {{ t("buttons.select") }}
        </button>
      </div>
    </BaseModal>
  </teleport>
</template>

<style lang="scss" scoped>
.ai-image-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(6rem, 1fr));
  gap: 1rem;

  @include media-breakpoint-up(sm) {
    grid-template-columns: repeat(auto-fill, minmax(8rem, 1fr));
  }
}

.search-modal-body {
  overflow-y: auto;
  padding-top: $spacer-1;

  @include media-breakpoint-up(sm) {
    max-height: 50vh;
  }
}
</style>
